'use client';

import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';
import { useSearchParams, useRouter } from 'next/navigation';
import { useSavedSearches, SavedSearchFilter } from '@/lib/hooks/useSavedSearches';
import { useAuth } from '@/contexts/AuthContext';
import {
  searchAllContent,
  getFeaturedContent,
  SearchResult,
  SearchCategory,
} from '@/lib/search/searchService';

// Extend SearchCategory locally to include UI-only categories
type ExtendedCategory = SearchCategory | 'heritage' | 'event';

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORIES: { id: SearchCategory; label: string; icon: string }[] = [
  { id: 'all', label: 'All', icon: '🔍' },
  { id: 'package', label: 'Packages', icon: '🏔️' },
  { id: 'hotel', label: 'Hotels', icon: '🏨' },
  { id: 'guide', label: 'Guides', icon: '🧭' },
  { id: 'vehicle', label: 'Vehicles', icon: '🚗' },
  { id: 'destination', label: 'Destinations', icon: '📍' },
  { id: 'flight', label: 'Flights', icon: '✈️' },
  { id: 'restaurant', label: 'Restaurants', icon: '🍽️' },
  { id: 'place', label: 'Hidden Gems', icon: '🗺️' },
  { id: 'community', label: 'Community', icon: '🏘️' },
  { id: 'heritage', label: 'Heritage', icon: '🏛️' },
  { id: 'event', label: 'Events', icon: '🎉' },
];

const SORT_OPTIONS = [
  { id: 'relevance', label: 'Most Relevant' },
  { id: 'price_low', label: 'Price: Low to High' },
  { id: 'price_high', label: 'Price: High to Low' },
  { id: 'rating', label: 'Highest Rated' },
];

const PRICE_RANGES = [
  { id: 'all', label: 'Any Price' },
  { id: '0-5000', label: 'Under NPR 5,000' },
  { id: '5000-20000', label: 'NPR 5,000 – 20,000' },
  { id: '20000-50000', label: 'NPR 20,000 – 50,000' },
  { id: '50000+', label: 'NPR 50,000+' },
];

const NEPAL_PROVINCES = ['All Nepal', 'Koshi', 'Madhesh', 'Bagmati', 'Gandaki', 'Lumbini', 'Karnali', 'Sudurpashchim'];
const ADVENTURE_LEVELS = ['Any Level', 'Easy', 'Moderate', 'Challenging', 'Extreme'];
const SEASONS = ['Any Season', 'Spring (Mar-May)', 'Summer (Jun-Aug)', 'Autumn (Sep-Nov)', 'Winter (Dec-Feb)'];
const DURATIONS = ['Any Duration', '1-3 Days', '4-7 Days', '8-14 Days', '15+ Days'];

// Pagination page size
const PAGE_SIZE = 12;

// Suggested searches — only show ones that have real data or are well-known
const SUGGESTED_SEARCHES = [
  'Everest Base Camp',
  'Annapurna Circuit',
  'Pokhara Lakeside',
  'Kathmandu Heritage',
  'Mustang Desert',
  'Chitwan Safari',
];

// Demo fallback data shown when DB tables are empty
const DEMO_RESULTS: SearchResult[] = [
  { id: 'demo-pkg-1', type: 'package', name: 'Everest Base Camp Trek', description: '14-day classic EBC trek with experienced guides', location: 'Solukhumbu, Koshi', price: 85000, rating: 4.9, image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=600&q=80', duration: '14 Days', is_demo: true, slug: 'everest-base-camp' },
  { id: 'demo-pkg-2', type: 'package', name: 'Annapurna Circuit', description: '18-day circuit through diverse landscapes', location: 'Gandaki Province', price: 72000, rating: 4.8, image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80', duration: '18 Days', is_demo: true, slug: 'annapurna-circuit' },
  { id: 'demo-pkg-3', type: 'package', name: 'Pokhara Lakeside Retreat', description: 'Relaxing 5-day stay at Phewa Lake, Pokhara Lakeside', location: 'Pokhara, Gandaki', price: 25000, rating: 4.7, image: 'https://images.unsplash.com/photo-1605640840605-14ac1855827b?w=600&q=80', duration: '5 Days', is_demo: true, slug: 'pokhara-luxury' },
  { id: 'demo-hotel-1', type: 'hotel', name: 'Yak & Yeti Hotel', description: 'Luxury 5-star hotel in the heart of Kathmandu', location: 'Kathmandu', price: 12000, rating: 4.7, image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80', is_demo: true },
  { id: 'demo-hotel-2', type: 'hotel', name: 'Fish Tail Lodge Pokhara', description: 'Iconic lake-side lodge in Pokhara Lakeside near Phewa Lake', location: 'Pokhara Lakeside, Gandaki', price: 8500, rating: 4.8, image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=600&q=80', is_demo: true },
  { id: 'demo-hotel-3', type: 'hotel', name: 'Lakeside View Hotel', description: 'Budget-friendly hotel with stunning Phewa Lake views in Pokhara', location: 'Lakeside, Pokhara', price: 3500, rating: 4.3, image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600&q=80', is_demo: true },
  { id: 'demo-guide-1', type: 'guide', name: 'Ram Sherpa', description: 'Expert Everest guide with 12 years experience', location: 'Solukhumbu', price: 3500, rating: 4.97, image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&q=80', verified: true, is_demo: true },
  { id: 'demo-guide-2', type: 'guide', name: 'Maya Gurung', description: 'Senior guide specialising in Pokhara, Annapurna & cultural tours', location: 'Pokhara, Gandaki', price: 4000, rating: 4.98, image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&q=80', verified: true, is_demo: true },
  { id: 'demo-dest-1', type: 'destination', name: 'Pokhara', description: 'Gateway to the Annapurna region, home to Phewa Lake and Lakeside', location: 'Gandaki Province', image: 'https://images.unsplash.com/photo-1605640840605-14ac1855827b?w=600&q=80', is_demo: true, slug: 'pokhara' },
  { id: 'demo-dest-2', type: 'destination', name: 'Chitwan', description: 'Wildlife safari and jungle adventures', location: 'Bagmati Province', image: 'https://images.unsplash.com/photo-1549366021-9f761d450615?w=600&q=80', is_demo: true, slug: 'chitwan' },
  { id: 'demo-dest-3', type: 'destination', name: 'Kathmandu', description: 'Nepal\'s capital — heritage temples, culture and history', location: 'Bagmati Province', image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80', is_demo: true, slug: 'kathmandu' },
  { id: 'demo-vehicle-1', type: 'vehicle', name: 'Toyota Hiace Van', description: 'Comfortable 12-seater van for group travel across Nepal', location: 'Kathmandu', price: 8000, rating: 4.6, image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80', is_demo: true },
  { id: 'demo-vehicle-2', type: 'vehicle', name: 'Mahindra Thar Jeep', description: 'Rugged 4WD jeep for mountain and off-road adventures', location: 'Pokhara', price: 6500, rating: 4.8, image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=600&q=80', is_demo: true },
  { id: 'demo-place-1', type: 'place', name: 'Phewa Lake Viewpoint', description: 'Hidden viewpoint overlooking Phewa Lake in Pokhara Lakeside', location: 'Pokhara, Gandaki', is_demo: true },
  { id: 'demo-place-2', type: 'place', name: 'Gosaikunda Lake', description: 'Sacred alpine lake at 4,380m — hidden gem in Langtang', location: 'Rasuwa, Bagmati', is_demo: true },
  { id: 'demo-community-1', type: 'community', name: 'Thamel Community Hub', description: 'Cultural meeting point in Kathmandu\'s tourist district', location: 'Kathmandu', is_demo: true },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatNPR(n: number) {
  return `NPR ${(n || 0).toLocaleString('en-NP')}`;
}

function getTypeIcon(type: string) {
  const icons: Record<string, string> = {
    package: '🏔️', hotel: '🏨', guide: '🧭', vehicle: '🚗', destination: '📍',
    place: '🗺️', flight: '✈️', restaurant: '🍽️', community: '🏘️', heritage: '🏛️', event: '🎉',
  };
  return icons[type] || '🔍';
}

function getTypeLabel(type: string) {
  const labels: Record<string, string> = {
    package: 'Package', hotel: 'Hotel', guide: 'Guide', vehicle: 'Vehicle',
    destination: 'Destination', place: 'Hidden Gem', flight: 'Flight',
    restaurant: 'Restaurant', community: 'Community', heritage: 'Heritage', event: 'Event',
  };
  return labels[type] || type;
}

function getItemHref(item: SearchResult) {
  if (item.type === 'flight') return '/flights';
  if (item.is_demo) {
    if (item.type === 'package') return item.slug ? `/packages/${item.slug}` : '/packages';
    if (item.type === 'hotel') return '/hotels';
    if (item.type === 'guide') return '/guides';
    if (item.type === 'vehicle') return '/vehicles';
    if (item.type === 'destination') return item.slug ? `/destinations/${item.slug}` : '/destinations';
    if (item.type === 'place') return '/discover';
    if (item.type === 'community') return '/discover';
    return '#';
  }
  if (item.type === 'package') return `/packages/${item.slug || item.id}`;
  if (item.type === 'hotel') return `/hotels/${item.id}`;
  if (item.type === 'guide') return `/guides/${item.id}`;
  if (item.type === 'vehicle') return `/vehicles/${item.id}`;
  if (item.type === 'destination') return `/destinations/${item.slug || item.id}`;
  if (item.type === 'place') return `/discover/${item.id}`;
  if (item.type === 'community') return `/discover/${item.id}`;
  return '#';
}

// Filter demo results by query using multi-word matching
function filterDemoResults(query: string, category: SearchCategory): SearchResult[] {
  const q = query.toLowerCase().trim();
  const words = q.split(/\s+/).filter(Boolean);

  return DEMO_RESULTS.filter(item => {
    // Category filter — map heritage/event/community to place/community
    if (category !== 'all') {
      if (category === 'heritage' || category === 'event') {
        if (item.type !== 'place' && item.type !== 'community') return false;
      } else if (item.type !== category) return false;
    }
    if (!q) return true;

    const searchable = [item.name, item.description, item.location, item.category]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    // Match if any word matches
    return words.some(w => searchable.includes(w));
  });
}

// ─── Save Filter Modal ────────────────────────────────────────────────────────
interface SaveFilterModalProps {
  onSave: (name: string) => void;
  onClose: () => void;
  defaultName: string;
}

function SaveFilterModal({ onSave, onClose, defaultName }: SaveFilterModalProps) {
  const [name, setName] = useState(defaultName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave(name.trim());
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(11,35,72,0.85)' }}>
      <div className="glass-panel rounded-2xl p-6 w-full max-w-sm shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--himal-white)' }}>💾 Save This Search</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" style={{ color: 'rgba(245,240,232,0.5)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Budget Pokhara Hotels"
            maxLength={40}
            className="w-full px-4 py-2.5 rounded-xl text-sm mb-4 focus:outline-none focus:ring-2"
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'var(--himal-white)', caretColor: 'var(--gold-bright)' }}
            onKeyDown={e => { if (e.key === 'Escape') onClose(); }}
          />
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl text-xs font-medium filter-chip">Cancel</button>
            <button type="submit" disabled={!name.trim()} className="flex-1 py-2.5 rounded-xl text-xs font-semibold btn-gold disabled:opacity-40 disabled:cursor-not-allowed">Save Filter</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Result Card ──────────────────────────────────────────────────────────────
function ResultCard({ item }: { item: SearchResult }) {
  const href = getItemHref(item);
  return (
    <Link href={href} className="block group" style={{ textDecoration: 'none' }}>
      <div
        className="rounded-2xl overflow-hidden transition-all duration-300 group-hover:scale-[1.02] group-hover:shadow-xl flex flex-col"
        style={{ background: 'rgba(27,58,107,0.72)', border: '1px solid rgba(245,240,232,0.1)', minHeight: 0 }}
      >
        {/* Image */}
        <div className="relative overflow-hidden flex-shrink-0" style={{ height: '176px' }}>
          {item.image ? (
            <img
              src={item.image}
              alt={`${item.name} - ${getTypeLabel(item.type)} in Nepal`}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-4xl" style={{ background: 'rgba(35,74,133,0.5)' }}>
              {getTypeIcon(item.type)}
            </div>
          )}
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(27,58,107,0.85) 0%, transparent 60%)' }} />
          {/* Category badge */}
          <div className="absolute top-3 left-3 flex items-center gap-1.5" style={{ maxWidth: 'calc(100% - 80px)' }}>
            <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full" style={{ background: 'rgba(26,188,212,0.2)', border: '1px solid rgba(26,188,212,0.35)', color: 'var(--glacier-light)', whiteSpace: 'nowrap' }}>
              {getTypeIcon(item.type)} {getTypeLabel(item.type)}
            </span>
            {item.is_demo && (
              <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(245,192,48,0.15)', border: '1px solid rgba(245,192,48,0.25)', color: 'var(--gold-bright)', whiteSpace: 'nowrap' }}>Demo</span>
            )}
          </div>
          {/* Rating */}
          {item.rating != null && item.rating > 0 && (
            <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: 'rgba(27,58,107,0.8)', border: '1px solid rgba(245,240,232,0.15)' }}>
              <span className="text-[10px]" style={{ color: 'var(--gold-bright)' }}>★</span>
              <span className="text-[10px] font-medium" style={{ color: 'var(--himal-white)' }}>{Number(item.rating).toFixed(1)}</span>
            </div>
          )}
          {/* Verified badge */}
          {item.verified && (
            <div className="absolute bottom-3 right-3">
              <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(33,181,115,0.2)', border: '1px solid rgba(33,181,115,0.3)', color: '#21B573' }}>✓ Verified</span>
            </div>
          )}
        </div>
        {/* Content — fixed min-height to prevent clipping */}
        <div className="flex flex-col" style={{ padding: '16px', paddingBottom: '20px', minHeight: '140px' }}>
          <h3 className="font-semibold text-sm mb-1" style={{ color: 'var(--himal-white)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: '1.4' }}>{item.name}</h3>
          {item.location && (
            <div className="text-xs mb-2 flex items-center gap-1" style={{ color: 'rgba(245,240,232,0.5)' }}>
              <span style={{ flexShrink: 0 }}>📍</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.location}</span>
            </div>
          )}
          {item.description && (
            <p className="text-xs mb-3 flex-1" style={{ color: 'rgba(245,240,232,0.6)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: '1.5' }}>{item.description}</p>
          )}
          {/* Footer row — always at bottom, never clipped */}
          <div className="flex items-center justify-between gap-2 mt-auto" style={{ paddingTop: '10px', borderTop: '1px solid rgba(245,240,232,0.07)', flexWrap: 'nowrap' }}>
            {item.price != null && item.price > 0 ? (
              <span className="text-sm font-semibold" style={{ color: 'var(--gold-bright)', flexShrink: 0 }}>{formatNPR(item.price)}</span>
            ) : <span />}
            <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
              {item.duration && <span className="text-xs" style={{ color: 'rgba(245,240,232,0.5)' }}>⏱ {item.duration}</span>}
              {/* View Details — 44px touch target */}
              <span
                className="text-xs font-medium rounded-lg"
                style={{ background: 'rgba(26,188,212,0.12)', color: 'var(--glacier-light)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: '36px', padding: '0 12px', whiteSpace: 'nowrap' }}
              >
                View Details
              </span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

// ─── Skeleton Card ────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(27,58,107,0.5)', border: '1px solid rgba(245,240,232,0.07)' }}>
      <div className="h-44 animate-pulse" style={{ background: 'rgba(35,74,133,0.5)' }} />
      <div className="p-4 space-y-2">
        <div className="h-4 rounded-lg animate-pulse w-3/4" style={{ background: 'rgba(35,74,133,0.6)' }} />
        <div className="h-3 rounded-lg animate-pulse w-1/2" style={{ background: 'rgba(35,74,133,0.4)' }} />
        <div className="h-3 rounded-lg animate-pulse w-full" style={{ background: 'rgba(35,74,133,0.4)' }} />
        <div className="h-3 rounded-lg animate-pulse w-2/3" style={{ background: 'rgba(35,74,133,0.3)' }} />
      </div>
    </div>
  );
}

// ─── Main Search Page ─────────────────────────────────────────────────────────
function SearchPageInner() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { user } = useAuth();

  // Initialize from URL params
  const [query, setQuery] = useState(() => searchParams?.get('q') || '');
  const [activeCategory, setActiveCategory] = useState<SearchCategory>(() => (searchParams?.get('category') as SearchCategory) || 'all');
  const [sortBy, setSortBy] = useState('relevance');
  const [priceRange, setPriceRange] = useState('all');
  const [province, setProvince] = useState(searchParams?.get('province') || 'All Nepal');
  const [adventureLevel, setAdventureLevel] = useState('Any Level');
  const [season, setSeason] = useState('Any Season');
  const [duration, setDuration] = useState('Any Duration');

  // Search state
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [featured, setFeatured] = useState<SearchResult[]>([]);
  const [trending, setTrending] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [usingDemoData, setUsingDemoData] = useState(false);

  // Pagination state
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);

  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [activeSavedId, setActiveSavedId] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Debounce ref
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  // Track the latest search to prevent stale results
  const searchIdRef = useRef(0);

  const { savedSearches, saveSearch, deleteSearch } = useSavedSearches(user?.id ?? null);

  // ── URL sync ──────────────────────────────────────────────────────────────
  const updateURL = useCallback((q: string, cat: SearchCategory) => {
    const params = new URLSearchParams();
    if (q.trim()) params.set('q', q.trim());
    if (cat !== 'all') params.set('category', cat);
    const newUrl = `/search${params.toString() ? '?' + params.toString() : ''}`;
    router.replace(newUrl, { scroll: false });
  }, [router]);

  // ── Suggestions ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!query.trim()) { setSuggestions([]); return; }
    const q = query.toLowerCase();
    setSuggestions(SUGGESTED_SEARCHES.filter(s => s.toLowerCase().includes(q)).slice(0, 5));
  }, [query]);

  // ── Close suggestions on outside click ───────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!searchRef.current?.contains(e.target as Node)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Load search history ───────────────────────────────────────────────────
  useEffect(() => {
    try {
      const h = JSON.parse(localStorage.getItem('yn_search_history') || '[]');
      setSearchHistory(h.slice(0, 5));
    } catch {}
  }, []);

  // ── Load featured content on mount ────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setInitialLoading(true);
      try {
        const { featured: f, trending: t } = await getFeaturedContent();
        if (f.length === 0 && t.length === 0) {
          setFeatured(DEMO_RESULTS.slice(0, 6));
          setTrending(DEMO_RESULTS.slice(6, 10));
          setUsingDemoData(true);
        } else {
          setFeatured(f);
          setTrending(t);
          setUsingDemoData(false);
        }
      } catch {
        setFeatured(DEMO_RESULTS.slice(0, 6));
        setTrending(DEMO_RESULTS.slice(6, 10));
        setUsingDemoData(true);
      }
      setInitialLoading(false);
    };
    load();
  }, []);

  // ── Perform search (debounced) ────────────────────────────────────────────
  const runSearch = useCallback(async (q: string, cat: SearchCategory) => {
    if (!q.trim()) {
      setSearchResults([]);
      setSearchError(null);
      setLoading(false);
      return;
    }

    // Redirect flights to flights page
    if (cat === 'flight') {
      router.push(`/flights?q=${encodeURIComponent(q)}`);
      return;
    }

    const thisSearchId = ++searchIdRef.current;
    setLoading(true);
    setSearchError(null);
    // Reset pagination on new search
    setVisibleCount(PAGE_SIZE);

    try {
      const results = await searchAllContent({
        query: q,
        category: cat,
        province,
        priceRange,
        sortBy,
        limit: 100, // fetch all, paginate client-side
      });

      // Ignore stale results
      if (thisSearchId !== searchIdRef.current) return;

      if (results.length === 0) {
        // Fall back to demo data filtered by query
        const demoFiltered = filterDemoResults(q, cat);
        setSearchResults(demoFiltered);
        setUsingDemoData(demoFiltered.length > 0);
      } else {
        setSearchResults(results);
        setUsingDemoData(false);
      }
    } catch (err) {
      if (thisSearchId !== searchIdRef.current) return;
      // On error, show filtered demo data
      const demoFiltered = filterDemoResults(q, cat);
      setSearchResults(demoFiltered);
      setUsingDemoData(demoFiltered.length > 0);
      setSearchError('Search encountered an issue. Showing demo results.');
    }

    if (thisSearchId === searchIdRef.current) {
      setLoading(false);
    }
  }, [province, priceRange, sortBy, router]);

  // ── Debounce query changes ────────────────────────────────────────────────
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim()) {
      setSearchResults([]);
      setLoading(false);
      return;
    }

    setLoading(true); // Show skeleton immediately
    debounceRef.current = setTimeout(() => {
      runSearch(query, activeCategory);
      updateURL(query, activeCategory);
    }, 350);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, activeCategory, runSearch, updateURL]);

  // ── Re-run search when filters change (if query exists) ───────────────────
  useEffect(() => {
    if (query.trim()) {
      runSearch(query, activeCategory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [province, priceRange, sortBy]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const saveSearchHistory = (q: string) => {
    if (!q.trim()) return;
    try {
      const h = JSON.parse(localStorage.getItem('yn_search_history') || '[]');
      const updated = [q, ...h.filter((x: string) => x !== q)].slice(0, 10);
      localStorage.setItem('yn_search_history', JSON.stringify(updated));
      setSearchHistory(updated.slice(0, 5));
    } catch {}
  };

  const handleSearch = (q?: string) => {
    const searchQuery = q !== undefined ? q : query;
    saveSearchHistory(searchQuery);
    setShowSuggestions(false);
    if (q !== undefined) {
      setQuery(q);
    }
    // Force immediate search (bypass debounce)
    if (debounceRef.current) clearTimeout(debounceRef.current);
    runSearch(searchQuery, activeCategory);
    updateURL(searchQuery, activeCategory);
  };

  const handleCategoryChange = (cat: SearchCategory) => {
    setActiveCategory(cat);
    setActiveSavedId(null);
    setVisibleCount(PAGE_SIZE);
    if (cat === 'flight') {
      router.push(query.trim() ? `/flights?q=${encodeURIComponent(query)}` : '/flights');
      return;
    }
    updateURL(query, cat);
    if (query.trim()) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      runSearch(query, cat);
    }
  };

  const handleSuggestedSearch = (term: string) => {
    setQuery(term);
    setShowSuggestions(false);
    saveSearchHistory(term);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);
    setVisibleCount(PAGE_SIZE);
    runSearch(term, activeCategory);
    updateURL(term, activeCategory);
  };

  const clearSearch = () => {
    setQuery('');
    setSearchResults([]);
    setLoading(false);
    setSearchError(null);
    setShowSuggestions(false);
    setActiveSavedId(null);
    setVisibleCount(PAGE_SIZE);
    updateURL('', activeCategory);
  };

  // ── Load more handler ─────────────────────────────────────────────────────
  const handleLoadMore = useCallback(() => {
    setLoadingMore(true);
    // Simulate a brief delay for UX smoothness
    setTimeout(() => {
      setVisibleCount(prev => prev + PAGE_SIZE);
      setLoadingMore(false);
    }, 300);
  }, []);

  const handleVoiceSearch = () => {
    if (typeof window === 'undefined') return;
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Voice search not supported. Please use Chrome.');
      return;
    }
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SR();
    recognition.lang = 'en-US';
    setIsListening(true);
    recognition.start();
    recognition.onresult = (e: any) => {
      const transcript = e.results[0][0].transcript;
      setIsListening(false);
      handleSearch(transcript);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
  };

  const buildDefaultSaveName = useCallback(() => {
    const parts: string[] = [];
    if (query.trim()) parts.push(query.trim());
    if (province !== 'All Nepal') parts.push(province);
    if (activeCategory !== 'all') {
      const cat = CATEGORIES.find(c => c.id === activeCategory);
      if (cat) parts.push(cat.label);
    }
    if (priceRange !== 'all') {
      const pr = PRICE_RANGES.find(p => p.id === priceRange);
      if (pr) parts.push(pr.label);
    }
    return parts.join(' · ') || 'My Search';
  }, [query, province, activeCategory, priceRange]);

  const loadSavedSearch = useCallback((s: SavedSearchFilter) => {
    setQuery(s.query);
    setActiveCategory(s.category as SearchCategory);
    setProvince(s.province);
    setPriceRange(s.priceRange);
    setAdventureLevel(s.adventureLevel);
    setSeason(s.season);
    setDuration(s.duration);
    setSortBy(s.sortBy);
    setActiveSavedId(s.id);
    setShowFilters(false);
    setVisibleCount(PAGE_SIZE);
    if (s.query.trim()) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      runSearch(s.query, s.category as SearchCategory);
      updateURL(s.query, s.category as SearchCategory);
    }
  }, [runSearch, updateURL]);

  const handleSaveFilter = useCallback(async (name: string) => {
    const entry = await saveSearch(name, { query, category: activeCategory, province, priceRange, adventureLevel, season, duration, sortBy });
    setActiveSavedId(entry.id);
    setShowSaveModal(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2500);
  }, [query, activeCategory, province, priceRange, adventureLevel, season, duration, sortBy, saveSearch]);

  // ── Derived state ─────────────────────────────────────────────────────────
  const hasQuery = query.trim().length > 0;
  // Display results: if query exists use search results, else use featured
  const displayResults = hasQuery ? searchResults : featured;
  // Paginated slice
  const paginatedResults = displayResults.slice(0, visibleCount);
  const hasMore = visibleCount < displayResults.length;
  // Count is always from the actual displayed array
  const displayCount = displayResults.length;

  // State flags — mutually exclusive
  const isLoading = loading || (initialLoading && !hasQuery);
  const showEmpty = !isLoading && hasQuery && searchResults.length === 0;
  const showResults = !isLoading && displayResults.length > 0;
  const showTrending = !hasQuery && !isLoading && trending.length > 0;

  const hasActiveFilters = hasQuery || activeCategory !== 'all' || province !== 'All Nepal' || priceRange !== 'all';

  return (
    <div className="min-h-screen" style={{ background: 'var(--royal-blue)' }}>
      <Header />

      {showSaveModal && (
        <SaveFilterModal
          defaultName={buildDefaultSaveName()}
          onSave={handleSaveFilter}
          onClose={() => setShowSaveModal(false)}
        />
      )}

      <div className="pt-20">
        {/* ── Search Header ── */}
        <div className="py-8 px-4" style={{ background: 'linear-gradient(180deg, #234A85 0%, #1B3A6B 100%)' }}>
          <div className="max-w-4xl mx-auto">
            <div className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--glacier-light)' }}>Discover Nepal</div>
            <h1 className="font-serif text-2xl md:text-3xl font-semibold mb-5" style={{ color: 'var(--himal-white)' }}>
              Search <span className="italic" style={{ color: 'var(--gold-bright)' }}>Nepal & Beyond</span>
            </h1>

            {/* Demo notice */}
            {usingDemoData && (
              <div className="mb-4 px-4 py-2.5 rounded-xl flex items-center gap-2 text-xs" style={{ background: 'rgba(245,192,48,0.08)', border: '1px solid rgba(245,192,48,0.2)', color: 'rgba(245,192,48,0.8)' }}>
                <span>ℹ️</span>
                <span>Showing demo content — live data will appear once providers register on YatraNest.</span>
              </div>
            )}

            {/* Save success toast */}
            {saveSuccess && (
              <div className="mb-4 px-4 py-2.5 rounded-xl flex items-center gap-2 text-xs" style={{ background: 'rgba(33,181,115,0.1)', border: '1px solid rgba(33,181,115,0.25)', color: '#21B573' }}>
                <span>✓</span><span>Search filter saved!</span>
              </div>
            )}

            {/* ── Search bar ── */}
            <div ref={searchRef} className="relative mb-4">
              <div className="flex items-center gap-2 px-3 py-2 rounded-2xl" style={{ background: 'rgba(27,58,107,0.72)', border: '1px solid rgba(245,240,232,0.12)', backdropFilter: 'blur(20px)' }}>
                <button onClick={() => handleSearch()} className="p-2 rounded-xl hover:bg-white/10 transition-colors shrink-0" title="Search" aria-label="Search" style={{ minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--glacier)' }}>
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/>
                  </svg>
                </button>
                <input
                  type="text"
                  value={query}
                  onChange={e => { setQuery(e.target.value); setShowSuggestions(true); setActiveSavedId(null); }}
                  onFocus={() => setShowSuggestions(true)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleSearch();
                    if (e.key === 'Escape') setShowSuggestions(false);
                  }}
                  placeholder="Search destinations, hotels, guides, packages…"
                  className="flex-1 bg-transparent text-sm py-2 focus:outline-none"
                  style={{ color: 'var(--himal-white)', minWidth: 0 }}
                />
                <button
                  onClick={handleVoiceSearch}
                  className={`p-2 rounded-xl transition-all shrink-0 ${isListening ? 'animate-pulse' : 'hover:bg-white/10'}`}
                  style={{ color: isListening ? '#ef4444' : 'rgba(245,240,232,0.5)', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  title="Voice search"
                  aria-label="Voice search"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" y1="19" x2="12" y2="23"/>
                    <line x1="8" y1="23" x2="16" y2="23"/>
                  </svg>
                </button>
                {query && (
                  <button onClick={clearSearch} className="p-2 rounded-xl hover:bg-white/10 shrink-0" style={{ color: 'rgba(245,240,232,0.5)', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} aria-label="Clear search">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/></svg>
                  </button>
                )}
              </div>

              {/* Suggestions dropdown */}
              {showSuggestions && (query || searchHistory.length > 0) && (
                <div className="absolute top-full left-0 right-0 mt-2 rounded-2xl overflow-hidden z-50" style={{ background: 'rgba(18,42,90,0.97)', border: '1px solid rgba(245,240,232,0.12)', backdropFilter: 'blur(20px)' }}>
                  {!query && searchHistory.length > 0 && (
                    <div>
                      <div className="px-4 py-2 text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.4)' }}>Recent</div>
                      {searchHistory.map((h, i) => (
                        <button key={i} onClick={() => handleSuggestedSearch(h)} className="w-full text-left px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-white/5 transition-colors" style={{ color: 'rgba(245,240,232,0.8)', minHeight: '44px' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'rgba(245,240,232,0.3)', flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                          {h}
                        </button>
                      ))}
                    </div>
                  )}
                  {suggestions.length > 0 && suggestions.map((s, i) => (
                    <button key={i} onClick={() => handleSuggestedSearch(s)} className="w-full text-left px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-white/5 transition-colors" style={{ color: 'rgba(245,240,232,0.8)', minHeight: '44px' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--glacier)', flexShrink: 0 }}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/></svg>
                      {s}
                    </button>
                  ))}
                  {!query && (
                    <div className="px-4 py-3">
                      <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>🔥 Trending</div>
                      <div className="flex flex-wrap gap-2">
                        {SUGGESTED_SEARCHES.map((t, i) => (
                          <button key={i} onClick={() => handleSuggestedSearch(t)} className="text-xs px-3 py-1.5 rounded-full transition-colors hover:bg-white/10" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(245,240,232,0.7)', minHeight: '36px' }}>{t}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Category chips — horizontal scroll, no overlap ── */}
            <div style={{ marginLeft: '-1rem', marginRight: '-1rem' }}>
              <div
                className="flex gap-2.5 overflow-x-auto pb-2"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch', paddingLeft: '1rem', paddingRight: '1rem' }}
              >
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.id)}
                    aria-pressed={activeCategory === cat.id}
                    className="flex items-center gap-1.5 rounded-full text-xs font-medium transition-all duration-200"
                    style={{
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                      minHeight: '44px',
                      paddingLeft: '14px',
                      paddingRight: '14px',
                      background: activeCategory === cat.id ? 'linear-gradient(135deg, var(--gold), var(--gold-light))' : 'rgba(255,255,255,0.07)',
                      border: activeCategory === cat.id ? '1px solid rgba(245,192,48,0.4)' : '1px solid rgba(255,255,255,0.1)',
                      color: activeCategory === cat.id ? '#071A38' : 'rgba(245,240,232,0.75)',
                      fontWeight: activeCategory === cat.id ? '600' : '500',
                    }}
                  >
                    <span style={{ fontSize: '14px', lineHeight: 1 }}>{cat.icon}</span>
                    <span>{cat.label}</span>
                  </button>
                ))}
                <div style={{ flexShrink: 0, width: '1rem' }} aria-hidden="true" />
              </div>
            </div>

            {/* Suggested searches */}
            <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
              <span className="text-[10px] font-semibold uppercase tracking-wider shrink-0" style={{ color: 'rgba(245,240,232,0.4)' }}>Try:</span>
              {SUGGESTED_SEARCHES.slice(0, 4).map((term, i) => (
                <button
                  key={i}
                  onClick={() => handleSuggestedSearch(term)}
                  className="text-xs px-3 py-1.5 rounded-full transition-colors hover:bg-white/10"
                  style={{ flexShrink: 0, whiteSpace: 'nowrap', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(245,240,232,0.7)', minHeight: '36px' }}
                >
                  {term}
                </button>
              ))}
            </div>

            {/* Saved searches */}
            {savedSearches.length > 0 && (
              <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
                <span className="text-[10px] font-semibold uppercase tracking-wider shrink-0" style={{ color: 'rgba(245,240,232,0.4)' }}>Saved:</span>
                {savedSearches.map(s => (
                  <div key={s.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium group" style={{ flexShrink: 0, whiteSpace: 'nowrap', background: activeSavedId === s.id ? 'rgba(212,175,55,0.12)' : 'rgba(255,255,255,0.06)', border: activeSavedId === s.id ? '1px solid rgba(212,175,55,0.4)' : '1px solid rgba(255,255,255,0.1)', color: activeSavedId === s.id ? 'var(--gold)' : 'rgba(245,240,232,0.7)' }}>
                    <button onClick={() => loadSavedSearch(s)} className="flex items-center gap-1.5" style={{ minHeight: '36px' }}>
                      <span>🔖</span><span>{s.name}</span>
                    </button>
                    <button onClick={e => { e.stopPropagation(); deleteSearch(s.id); }} className="ml-0.5 opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-400" style={{ color: 'rgba(245,240,232,0.4)', minWidth: '24px', minHeight: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/></svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Main content ── */}
        {/* pb-safe: 32px base + env(safe-area-inset-bottom) for iOS notch */}
        <div className="max-w-7xl mx-auto px-4 py-8" style={{ paddingBottom: 'calc(120px + env(safe-area-inset-bottom, 0px))' }}>
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Filters sidebar */}
            <div className={`lg:w-64 shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}>
              <div className="rounded-2xl p-5 sticky top-24" style={{ background: 'rgba(27,58,107,0.72)', border: '1px solid rgba(245,240,232,0.1)', backdropFilter: 'blur(20px)' }}>
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--himal-white)' }}>Filters</h3>
                  <button onClick={() => { setProvince('All Nepal'); setPriceRange('all'); setAdventureLevel('Any Level'); setSeason('Any Season'); setDuration('Any Duration'); setActiveSavedId(null); }} className="text-xs" style={{ color: 'var(--glacier)', minHeight: '36px', padding: '0 8px' }}>Reset All</button>
                </div>

                {[
                  { label: 'Province', value: province, setter: setProvince, options: NEPAL_PROVINCES },
                  { label: 'Adventure Level', value: adventureLevel, setter: setAdventureLevel, options: ADVENTURE_LEVELS },
                  { label: 'Duration', value: duration, setter: setDuration, options: DURATIONS },
                  { label: 'Best Season', value: season, setter: setSeason, options: SEASONS },
                  { label: 'Sort By', value: sortBy, setter: setSortBy, options: SORT_OPTIONS.map(s => s.label), valueMap: SORT_OPTIONS.reduce((acc, s) => ({ ...acc, [s.label]: s.id }), {} as Record<string, string>) },
                ].map(({ label, value, setter, options }) => (
                  <div key={label} className="mb-5">
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--gold)' }}>{label}</label>
                    <select
                      value={value}
                      onChange={e => { setter(e.target.value); setActiveSavedId(null); }}
                      className="w-full px-3 py-2.5 rounded-xl text-sm focus:outline-none"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--himal-white)', minHeight: '44px' }}
                    >
                      {options.map(o => <option key={o} value={o} style={{ background: '#1B3A6B' }}>{o}</option>)}
                    </select>
                  </div>
                ))}

                <div className="mb-5">
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--gold)' }}>Budget</label>
                  <div className="space-y-1.5">
                    {PRICE_RANGES.map(r => (
                      <button key={r.id} onClick={() => { setPriceRange(r.id); setActiveSavedId(null); }} className="w-full text-left px-3 py-2 rounded-xl text-xs transition-all" style={{ background: priceRange === r.id ? 'rgba(245,192,48,0.12)' : 'rgba(255,255,255,0.04)', border: priceRange === r.id ? '1px solid rgba(245,192,48,0.3)' : '1px solid rgba(255,255,255,0.07)', color: priceRange === r.id ? 'var(--gold-bright)' : 'rgba(245,240,232,0.7)', minHeight: '44px' }}>
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pt-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                  <button
                    onClick={() => setShowSaveModal(true)}
                    disabled={!hasActiveFilters}
                    className="w-full mt-2 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    style={{ background: hasActiveFilters ? 'rgba(212,175,55,0.12)' : 'transparent', border: '1px solid rgba(212,175,55,0.25)', color: 'var(--gold)', minHeight: '44px' }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                    Save This Search
                  </button>
                </div>
              </div>
            </div>

            {/* ── Results area ── */}
            <div className="flex-1 min-w-0">
              {/* Results header bar */}
              <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
                {/* Mobile filter toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden flex items-center gap-2 px-4 rounded-xl text-sm font-medium transition-colors"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(245,240,232,0.8)', minHeight: '44px' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg>
                  Filters
                </button>

                {/* Result count — always from actual data, never shows 0 while loading */}
                <div className="flex items-center gap-2 ml-auto">
                  {isLoading ? (
                    <div className="h-4 w-32 rounded animate-pulse" style={{ background: 'rgba(35,74,133,0.5)' }} />
                  ) : (
                    <span className="text-sm" style={{ color: 'rgba(245,240,232,0.6)' }}>
                      {hasQuery
                        ? `${displayCount} result${displayCount !== 1 ? 's' : ''} for "${query.trim()}"`
                        : `${displayCount} featured listing${displayCount !== 1 ? 's' : ''}`
                      }
                    </span>
                  )}
                  {hasActiveFilters && !isLoading && (
                    <button
                      onClick={() => setShowSaveModal(true)}
                      className="flex items-center gap-1.5 px-3 rounded-xl text-xs font-medium transition-all"
                      style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.2)', color: 'var(--gold)', minHeight: '44px' }}
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                      Save
                    </button>
                  )}
                </div>
              </div>

              {/* Error banner */}
              {searchError && !isLoading && (
                <div className="mb-4 px-4 py-3 rounded-xl flex items-center gap-2 text-xs" style={{ background: 'rgba(245,116,66,0.08)', border: '1px solid rgba(245,116,66,0.2)', color: 'rgba(245,116,66,0.9)' }}>
                  <span>⚠️</span>
                  <span>{searchError}</span>
                  <button onClick={() => runSearch(query, activeCategory)} className="ml-auto underline" style={{ minHeight: '36px' }}>Retry</button>
                </div>
              )}

              {/* ── LOADING: show skeletons only ── */}
              {isLoading && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                  {[1,2,3,4,5,6].map(i => <SkeletonCard key={i} />)}
                </div>
              )}

              {/* ── EMPTY: show once, only when not loading ── */}
              {showEmpty && (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="text-5xl mb-4">🔍</div>
                  <div className="text-lg font-semibold mb-2" style={{ color: 'var(--himal-white)' }}>No results found</div>
                  <p className="text-sm mb-2" style={{ color: 'rgba(245,240,232,0.5)' }}>
                    No matches for <strong style={{ color: 'var(--gold-bright)' }}>"{query.trim()}"</strong>
                  </p>
                  <p className="text-xs mb-6" style={{ color: 'rgba(245,240,232,0.4)' }}>
                    Try a different keyword, or browse a suggested search below.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center mb-6">
                    {SUGGESTED_SEARCHES.slice(0, 4).map(t => (
                      <button key={t} onClick={() => handleSuggestedSearch(t)} className="text-xs px-3 py-1.5 rounded-full transition-colors hover:bg-white/10" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(245,240,232,0.7)', minHeight: '44px' }}>{t}</button>
                    ))}
                  </div>
                  <button onClick={clearSearch} className="text-xs underline" style={{ color: 'var(--glacier)', minHeight: '44px' }}>Clear search</button>
                </div>
              )}

              {/* ── RESULTS: show only when not loading and results exist ── */}
              {showResults && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                    {paginatedResults.map(item => (
                      <ResultCard key={`${item.type}-${item.id}`} item={item} />
                    ))}
                  </div>

                  {/* ── Load More / Pagination ── */}
                  {hasMore && (
                    <div className="mt-8 flex flex-col items-center gap-3">
                      {/* Progress indicator */}
                      <p className="text-xs" style={{ color: 'rgba(245,240,232,0.45)' }}>
                        Showing {paginatedResults.length} of {displayCount} results
                      </p>
                      {/* Progress bar */}
                      <div className="w-full max-w-xs rounded-full overflow-hidden" style={{ height: '3px', background: 'rgba(255,255,255,0.08)' }}>
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${Math.round((paginatedResults.length / displayCount) * 100)}%`, background: 'linear-gradient(90deg, var(--glacier), var(--gold))' }}
                        />
                      </div>
                      {/* Load More button */}
                      <button
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        className="flex items-center gap-2 px-8 rounded-2xl text-sm font-semibold transition-all disabled:opacity-60"
                        style={{ background: 'rgba(26,188,212,0.12)', border: '1px solid rgba(26,188,212,0.25)', color: 'var(--glacier-light)', minHeight: '48px' }}
                      >
                        {loadingMore ? (
                          <>
                            <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                            Loading…
                          </>
                        ) : (
                          <>
                            Load {Math.min(PAGE_SIZE, displayCount - visibleCount)} more
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12l7 7 7-7" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {/* All results loaded indicator */}
                  {!hasMore && displayCount > PAGE_SIZE && (
                    <div className="mt-8 flex flex-col items-center gap-2">
                      <div className="w-full max-w-xs rounded-full overflow-hidden" style={{ height: '3px', background: 'rgba(255,255,255,0.08)' }}>
                        <div className="h-full w-full rounded-full" style={{ background: 'linear-gradient(90deg, var(--glacier), var(--gold))' }} />
                      </div>
                      <p className="text-xs" style={{ color: 'rgba(245,240,232,0.35)' }}>All {displayCount} results shown</p>
                    </div>
                  )}
                </>
              )}

              {/* ── TRENDING: only when no query, not loading ── */}
              {showTrending && (
                <div className="mt-10">
                  <div className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: 'var(--glacier-light)' }}>Popular This Week</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {trending.slice(0, 4).map(item => (
                      <Link key={`trending-${item.id}`} href={getItemHref(item)} className="block group rounded-xl overflow-hidden transition-transform duration-300 hover:scale-[1.02]" style={{ background: 'rgba(27,58,107,0.6)', border: '1px solid rgba(245,240,232,0.08)' }}>
                        <div className="relative h-32 overflow-hidden">
                          {item.image ? (
                            <img src={item.image} alt={`${item.name} trending destination`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-3xl" style={{ background: 'rgba(35,74,133,0.5)' }}>{getTypeIcon(item.type)}</div>
                          )}
                          <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(27,58,107,0.85), transparent)' }} />
                        </div>
                        <div className="p-3" style={{ paddingBottom: '14px' }}>
                          <div className="text-xs font-medium" style={{ color: 'var(--himal-white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                          <div className="text-[10px] mt-0.5" style={{ color: 'rgba(245,240,232,0.5)' }}>{getTypeLabel(item.type)}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default function SearchPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen" style={{ background: 'var(--royal-blue)' }}>
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="h-8 w-48 rounded-xl animate-pulse" style={{ background: 'rgba(35,74,133,0.5)' }} />
        </div>
      </div>
    }>
      <SearchPageInner />
    </Suspense>
  );
}
